import React, { useRef, useState } from 'react';
import { Box, Typography, TextField, Autocomplete as MUIAutocomplete, Chip } from '@mui/material';
import { UseFormSetValue, UseFormClearErrors, FieldErrors, FieldValues } from 'react-hook-form';
import '../index.css';

type TOption = {
  [key: string]: string | number;
};

interface IAutocomplete {
  options: TOption[];
  label?: string;
  setValue: UseFormSetValue<FieldValues>;
  name: string;
  title?: string;
  placeholder?: string;
  defaultValue?: TOption[];
  error?: FieldErrors;
  size?: 'small' | 'medium';
  clearErrors?: UseFormClearErrors<FieldValues>;
  isSetChips?: boolean;
  setFormData?: (name: string, value: any) => void | undefined;
  isShowButton: boolean;
  isAllowedAdd?: boolean;
  setError?: any;
}

const Autocomplete = ({
  options,
  label,
  setValue,
  name,
  title,
  placeholder,
  defaultValue = [],
  error = {},
  clearErrors,
  size,
  isSetChips,
  setFormData,
  isShowButton = true,
  isAllowedAdd = false,
  setError
}: IAutocomplete) => {
  const [selectedOptions, setSelectedOptions] = useState(defaultValue);
  const [customInputValue, setCustomInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOptionChange = (event: React.ChangeEvent<any> | null, newValue: any) => {
    setSelectedOptions(newValue);
    if (isSetChips && setFormData) {
      setFormData(name, newValue);
    } else {
      setValue(name, newValue);
    }
    if (newValue.length > 0 && clearErrors) clearErrors(name);
  };

  const handleInputChange = (event: React.ChangeEvent<any>, newInputValue: string) => {
    if (newInputValue.length <= 50) {
      if (clearErrors) clearErrors(name);
      setCustomInputValue(newInputValue);
    } else {
      setError(name, { type: 'string', message: 'Technical skill must be under 50 characters.' });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isAllowedAdd) {
      event.preventDefault();
      if (Object.keys(error).length > 0 && error[name]) {
        if (inputRef.current) {
          inputRef.current.blur();
        }
        setError(name, { type: 'string', message: 'Technical skill must be under 50 characters.' });
      } else {
        const newOption: TOption = { id: Date.now(), name: customInputValue };
        if (!options.some((option) => option.name === customInputValue)) {
          setSelectedOptions((prev) => [...prev, newOption]);
          setValue(name, [...selectedOptions, newOption]);
          setCustomInputValue('');
          if (clearErrors) clearErrors(name);
          if (inputRef.current) {
            inputRef.current.blur();
          }
        }
      }
    }
  };

  return (
    <Box className="autocomplete-container" marginTop={`${isShowButton ? '0' : 2}`}>
      {title && (
        <Typography variant="h6" textAlign="start" marginBottom="10px">
          {title}
        </Typography>
      )}
      <MUIAutocomplete
        multiple
        value={selectedOptions}
        onChange={handleOptionChange}
        options={options}
        getOptionLabel={(option: TOption) => option.name as string}
        renderInput={(params: any) => (
          <TextField
            {...params}
            label={label}
            size={size}
            placeholder={placeholder}
            error={!!error[name]}
            helperText={error[name]?.message}
            InputProps={{
              ...params.InputProps,
              endAdornment: null,
              inputRef: inputRef
            }}
            onKeyDown={handleKeyDown}
          />
        )}
        onInputChange={handleInputChange}
        noOptionsText={
          isAllowedAdd
            ? options.length > 0
              ? 'Press "Enter" to add new technology'
              : 'Loading technical skills, please wait...'
            : 'No option'
        }
      />
      <Box className="selected--chips" textAlign="start" paddingLeft={2}>
        {selectedOptions?.map((option: any) => (
          <Chip
            className="selected-chip"
            key={option.id}
            label={option.name}
            onDelete={() =>
              handleOptionChange(
                null,
                selectedOptions?.filter((item) => item.id !== option.id)
              )
            }
          />
        ))}
      </Box>
    </Box>
  );
};

export default Autocomplete;
